import Grid from '@mui/material/Grid';
import { ServiceListDraggable } from './service-list-draggable';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { useEffect, useState } from 'react';
import * as Yup from 'yup';

import { Button, Divider } from '@mui/material';
import { Service } from '../../../../../models/masters/service';
import { ServiceBundle } from '../../../../../models/service-bundle';
import { ApiResponse } from '../../../../../models/api-response';
import { useLoadData } from '../../../../../hooks/useLoadData';
import { serviceBundleApiKeyStore } from '../../../../../api/service-bundle-api-key-store';
import { useReactHookForm } from '../../../../../hooks/useReactHookForm';
import { ReactHookForm } from '../../../../../components/hook-form/react-hook-form.component';
import { BundleListFrom } from './service-bundle-list.form';
import { LoadingIndicator } from '../../../../../components/loading-indicator.component';
import { useSaveData } from '../../../../../hooks/useSaveData';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useDeleteData } from '../../../../../hooks/useDeleteData';
import { useAuthorize } from '../../../../../hooks/use-authorize';
import { UserPermissionName } from '../../../../../models/user-permission-name';

interface ServiceBundleSplitUp {
  splitEvenly: boolean;
  splitName: string;
  splitAmt: string;
  serviceBundleSplitItems: Service[];
  projectBundleSplitUpID: number;
  serviceBundleID: number;
  accuralAmount: number;
}

interface OriginalObject {
  bundleName: string;
  bundlePrice: number;
  officeID: number;
  serviceBundleID: number;
  serviceBundleSplitUps: ServiceBundleSplitUp[];
}
interface ServiceListGroup {
  serviceGroupID: number;
  serviceGroupName: string;

  services: Service[];
}
interface SaveServiceBundleFormProps {
  officeID: number;
  serviceBundle?: ServiceBundle;
  onCompleted: (value: ServiceBundle) => void;
  refetch: () => void;
}

const SaveServiceBundleValidationSchema = Yup.object().shape({
  bundleName: Yup.string().required('Bundle name is required'),
  bundlePrice: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? NaN : value))
    .typeError('Amount is required')
    .required('Amount is required'),
  serviceBundleSplitUps: Yup.array().of(
    Yup.object().shape({
      splitName: Yup.string().required('Split name is required'),
      splitAmt: Yup.string().required('Split amount is required'),
    })
  ),
});

export const SaveServiceBundleForm = ({
  officeID,
  serviceBundle,
  onCompleted,
  refetch,
}: SaveServiceBundleFormProps) => {
  const form = useReactHookForm({
    defaultValues: serviceBundle ? { ...serviceBundle, officeID } : { officeID },
    yupSchema: SaveServiceBundleValidationSchema,
  });
  const [services, setServices] = useState<any[]>([]);
  const [isVisible, setIsVisible] = useState(true);
  const [initialServices, setinitialServices] = useState<any[]>([]);
  const [isDeleteOpen, setIsDelete] = useState(false);
  const [currentService, setCurrentService] = useState<any>();
  const { data, isLoading } = useLoadData<ApiResponse<Service[]>>(serviceBundleApiKeyStore.getServices(0, officeID, 0));
  const {
    isLoading: isLoadingDelete,
    isError,
    error,
    mutateAsync: deleteService,
  } = useDeleteData(serviceBundleApiKeyStore?.deleteServicBundleItem(currentService?.bundleItemID));
  const { isLoading: isSaving, mutateAsync } = useSaveData<ServiceBundle>(
    serviceBundleApiKeyStore.createServiceBundle(),
    () => serviceBundleApiKeyStore.getServiceBundles(officeID)
  );

  const convertObject = (original: OriginalObject) => {
    return {
      serviceBundleID: original?.serviceBundleID,
      officeID: original.officeID,
      projectID: 0,
      unitID: 0,
      bundleName: original.bundleName,
      bundlePrice: String(original.bundlePrice),
      serviceBundleSplitUps: original.serviceBundleSplitUps.map((splitUp) => ({
        serviceBundleID: splitUp?.serviceBundleID,
        projectBundleSplitUpID: splitUp?.projectBundleSplitUpID,
        splitAmt: String(splitUp.splitAmt),
        splitName: splitUp.splitName,
        splitEvenly: splitUp.splitEvenly,
        serviceBundleSplitItems: splitUp.serviceBundleSplitItems.map((item) => ({
          projectBundleSplitUpID: item?.projectBundleSplitUpID,
          bundleItemID: item?.bundleItemID,
          serviceID: item.serviceID,
          serviceGroupID: item.serviceGroupID,
          serviceName: item.serviceName,
          parentTempSplitID: 'string',
          price: item?.price,
          accrualAmount: String(splitUp.accuralAmount),
          serviceGroupName: item.serviceGroupName,
          predecessorNames: item.predecessorNames || 'string',
        })),
        tempSplitID: 'string',
      })),
    };
  };

  const handleSubmit = async (value: any) => {
    const newObject = convertObject(value);

    const result = await mutateAsync({ data: newObject });
    onCompleted(result.data);
  };

  useEffect(() => {
    if (data?.data) {
      setServices(data?.data);
      setinitialServices(data?.data);
    }
  }, [data]);

  const handleIsVisibleDeleteButton = (value: boolean) => {
    setIsVisible(value);
  };

  const removeServiceFromGroups = (prevServices: ServiceListGroup[], serviceToRemove: Service): ServiceListGroup[] => {
    return prevServices.map((group) => {
      // Filter out the service with the specified serviceID
      const updatedServices = group?.services.filter(
        (service: Service) => service.serviceID !== serviceToRemove.serviceID
      );

      return {
        ...group,
        services: updatedServices, // Update the group's services with the filtered list
      };
    });
  };

  const handleDropCompleted = (item: Service) => {
    setServices((prevServices) => {
      return removeServiceFromGroups(prevServices, item);
    });
  };

  const addServiceToGroup = (
    serviceGroupIdentifier: string | number,
    newService: Service,
    services: ServiceListGroup[] = []
  ): ServiceListGroup[] => {
    let groupExists = false;

    const updatedServices = services.map((group) => {
      if (group.serviceGroupName === serviceGroupIdentifier || group.serviceGroupID === serviceGroupIdentifier) {
        groupExists = true;
        return {
          ...group,
          services: [...group.services, newService], // Add new service to the group's `services` array
        };
      }
      return group;
    });

    if (!groupExists) {
      // Add a new group if it does not exist
      return [
        ...updatedServices,
        {
          serviceGroupName: serviceGroupIdentifier.toString(),
          serviceGroupID: newService.serviceGroupID,
          services: [newService], // Initialize the group's `services` array with the new service
        },
      ];
    }

    return updatedServices;
  };

  const handleRemoveServiveFromBundle = async (value: Service) => {
    // setIsDelete(true);
    setCurrentService(value);
    // if (value) {
    //   setServices(addServiceToGroup(value?.serviceGroupName, value, services));
    //   // setServices([...services, value]);
    // }
    // handleDeleteBundleItem();
  };

  const handleDeleteBundleItem = async () => {
    setServices((prevServices) => addServiceToGroup(currentService?.serviceGroupName, currentService, prevServices));
    // setServices(addServiceToGroup(currentService?.serviceGroupName, currentService, services));
    if (currentService?.bundleItemID) {
      await deleteService();
      refetch();
    }

    setCurrentService(undefined);
    setIsDelete(false);
  };

  useEffect(() => {
    if (currentService) {
      handleDeleteBundleItem();
    }
  }, [currentService]);

  const handleRemoveSplitFromBundle = (item: ServiceBundleSplitUp) => {
    Array.isArray(item?.serviceBundleSplitItems) &&
      item?.serviceBundleSplitItems?.forEach((value: Service) =>
        setServices((prevServices) => addServiceToGroup(value?.serviceGroupName, value, prevServices))
      );
    // item?.serviceBundleSplitItems?.forEach((value: Service) =>
    //   setServices(addServiceToGroup(value?.serviceGroupName, value, services))
    // );
    refetch();
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <>
      <ReactHookForm
        formName="SEVE_BUNDLE_FORM"
        onSubmit={handleSubmit}
        formContextProps={form}
        isBusy={isLoading || isSaving}
      >
        <DndProvider backend={HTML5Backend}>
          <Grid className="bundlePopupMaxHeight">
            <Grid>
              <LoadingIndicator isLoading={isLoading} />
            </Grid>
            <Grid container spacing={2}>
              <Grid
                item
                lg={6}
                sm={6}
                xl={6}
                xs={6}
                style={{ borderRight: '10px solid #f1f1f1', paddingRight: '16px', paddingBottom: '16px' }}
                className="bundlePopupMaxHeight"
              >
                <BundleListFrom
                  handleRemoveSplitFromBundle={handleRemoveSplitFromBundle}
                  onDrop={handleDropCompleted}
                  onRemoveService={handleRemoveServiveFromBundle}
                  handleIsVisibleDeleteButton={handleIsVisibleDeleteButton}
                />
              </Grid>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                {data?.data && <ServiceListDraggable services={services} />}
              </Grid>
            </Grid>
          </Grid>
          <Divider style={{ marginBottom: '16px' }} />
        </DndProvider>
      </ReactHookForm>
      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <Button
          type="button"
          variant="contained"
          style={{
            backgroundColor: isReadOnlyPermission ? '#D7DBDD' : '#FF8A8A',
            color: isReadOnlyPermission ? 'black' : '#fff',
            width: '130px'
          }}
          onClick={() => form.handleSubmit(handleSubmit)()}
          disabled={isVisible || isReadOnlyPermission}
        >
          Save
        </Button>
      </div>
      {isDeleteOpen && (
        <div className="delete-btn-container">
          <Dialog className="modalpopup" title={'CONFIRM DELETE'} onClose={() => setIsDelete(false)}>
            <div>
              <div style={{ padding: '25px 50px' }}>
                <div>Are you sure you want to delete</div>
                <LoadingIndicator isLoading={isLoading} />
              </div>
            </div>
            <DialogActionsBar>
              <div className="d-flex justify-content-between">
                <button className="btn btn-sm btn-default  btn-lightGrey" onClick={() => setIsDelete(false)} autoFocus>
                  Cancel
                </button>
                <button
                  className="btn btn-sm btn-danger  btn-delete"
                  type="button"
                  style={{
                    backgroundColor: isReadOnlyPermission ? '#D7DBDD' : '#FF8A8A',
                    color: isReadOnlyPermission ? 'black' : '#fff',
                    marginLeft: 170 
                  }}
                  onClick={handleDeleteBundleItem}
                  disabled={false || isReadOnlyPermission}
                >
                  Delete
                </button>
              </div>
            </DialogActionsBar>
          </Dialog>
        </div>
      )}
    </>
  );
};
